import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Clock, ChevronDown } from "lucide-react";
import { DEFAULT_THEME } from "../constants/theme";

export default function CustomTimePicker({ value, onChange, className, theme = DEFAULT_THEME }) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    // Initial Scroll Refs
    const hourRef = useRef(null);
    const minuteRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        const handleEscKey = (event) => {
            if (event.key === "Escape" && isOpen) {
                setIsOpen(false);
                event.stopPropagation();
            }
        };

        // Removed scroll listener to prevent closing when scrolling internal lists
        // const handleScroll = () => { ... }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscKey, true);
        // window.addEventListener("scroll", handleScroll, true); 

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscKey, true);
            // window.removeEventListener("scroll", handleScroll, true);
        };
    }, [isOpen, isMobile]);


    const handleToggle = () => {
        if (!isOpen) {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const dropdownWidth = 200;
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;

                let left = rect.left;
                // Boundary Check (Right)
                if (left + dropdownWidth > screenWidth - 10) {
                    left = screenWidth - dropdownWidth - 10;
                }
                // Boundary Check (Left)
                if (left < 10) left = 10;

                let top = rect.bottom + 8;
                // Boundary Check (Bottom) - if it goes off screen, flip up? 
                // For simplicity, just letting it flow for now, usually modal is centered.

                setPosition({ top, left });
            }
            setIsOpen(true);

            // Auto-scroll to current values after render
            setTimeout(() => {
                scrollToCurrent();
            }, 0);
        } else {
            setIsOpen(false);
        }
    };

    const scrollToCurrent = () => {
        if (!value) return;
        const [h, m] = value.split(':').map(Number);

        if (hourRef.current) {
            const el = hourRef.current.querySelector(`[data-value="${h}"]`);
            if (el) el.scrollIntoView({ block: "center" });
        }
        if (minuteRef.current) {
            // Round minutes to nearest 5 or 1 for finding element?
            // Since we list 0, 5, 10... we match exactly or close
            // Actually input type=time usually allows any minute. 
            // Ideally we should list all minutes or 5-min intervals. 
            // Let's do 5-min intervals for cleaner UI, but handle 'exact' value display.
            const el = minuteRef.current.querySelector(`[data-value="${m}"]`) || minuteRef.current.querySelector(`[data-value="${Math.floor(m / 5) * 5}"]`);
            if (el) el.scrollIntoView({ block: "center" });
        }
    };

    const handleTimeChange = (type, val) => {
        let [h, m] = (value || "00:00").split(':');
        if (type === 'hour') h = String(val).padStart(2, '0');
        if (type === 'minute') m = String(val).padStart(2, '0');
        onChange(`${h}:${m}`);
    };

    // Generate Hours (0-23)
    const hours = Array.from({ length: 24 }, (_, i) => i);
    // Generate Minutes (0-55, step 5)
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, 10...

    const [currentH, currentM] = (value || "00:00").split(':').map(Number);

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div onClick={handleToggle} className="cursor-pointer w-full">
                <div className={`flex items-center justify-between bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl px-4 py-3.5 hover:bg-white hover:${theme.border} transition w-full group select-none`}>
                    <div className="flex items-center gap-2">
                        <Clock size={18} className={`text-gray-400 group-hover:${theme.text} transition-colors`} />
                        <span className={`font-bold text-sm ${value ? 'text-gray-700' : 'text-gray-400'}`}>
                            {value || "시간 선택"}
                        </span>
                    </div>
                    <ChevronDown size={16} className="text-gray-400" />
                </div>
            </div>

            {isOpen && isMobile && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-end justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
                    <div
                        className="relative bg-white w-full rounded-t-[2rem] p-6 pb-10 animate-slide-up-mobile shadow-2xl flex flex-col gap-5 transition-transform duration-200"
                        onTouchStart={(e) => {
                            containerRef.current.touchStartY = e.touches[0].clientY;
                        }}
                        onTouchMove={(e) => {
                            const currentY = e.touches[0].clientY;
                            const diff = currentY - containerRef.current.touchStartY;
                            if (diff > 0) {
                                e.currentTarget.style.transform = `translateY(${diff}px)`;
                            }
                        }}
                        onTouchEnd={(e) => {
                            const endY = e.changedTouches[0].clientY;
                            const diff = endY - containerRef.current.touchStartY;
                            if (diff > 100) {
                                setIsOpen(false);
                            } else {
                                e.currentTarget.style.transform = '';
                            }
                        }}
                    >
                        <div className="w-full flex justify-center py-2 -mt-4 mb-2 cursor-grab active:cursor-grabbing"
                        >
                            <div className="w-12 h-1.5 bg-gray-200 rounded-full" />
                        </div>
                        <div className="text-center mb-2">
                            <h3 className="text-lg font-bold text-gray-800">시간 선택</h3>
                        </div>

                        <div className="flex h-52 gap-2">
                            {/* Hours Column */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar rounded-2xl bg-gray-50/80 p-2" ref={hourRef}>
                                <div className="space-y-1">
                                    {hours.map(h => (
                                        <button
                                            key={h}
                                            data-value={h}
                                            type="button"
                                            onClick={() => handleTimeChange('hour', h)}
                                            className={`w-full py-3 rounded-xl text-base font-bold transition flex items-center justify-center
                                                        ${currentH === h
                                                    ? `${theme.bg} text-white shadow-md scale-95`
                                                    : "text-gray-400 hover:bg-gray-100"}`}
                                        >
                                            {h}시
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center justify-center text-gray-300 font-bold text-xl">:</div>

                            {/* Minutes Column */}
                            <div className="flex-1 overflow-y-auto custom-scrollbar rounded-2xl bg-gray-50/80 p-2" ref={minuteRef}>
                                <div className="space-y-1">
                                    {minutes.map(m => (
                                        <button
                                            key={m}
                                            data-value={m}
                                            type="button"
                                            onClick={() => handleTimeChange('minute', m)}
                                            className={`w-full py-3 rounded-xl text-base font-bold transition flex items-center justify-center
                                                        ${currentM === m
                                                    ? `${theme.bg} text-white shadow-md scale-95`
                                                    : "text-gray-400 hover:bg-gray-100"}`}
                                        >
                                            {String(m).padStart(2, '0')}분
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsOpen(false)}
                            className={`w-full py-4 rounded-2xl text-base font-bold ${theme.bg} text-white hover:opacity-90 transition shadow-lg mt-2`}
                        >
                            완료
                        </button>
                    </div>
                </div>,
                document.body
            )}

            {isOpen && !isMobile && (
                /* Desktop Dropdown - Inline Absolute (No Portal) */
                <div
                    ref={dropdownRef}
                    className="absolute top-full left-0 mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-2 w-[240px] flex flex-col gap-2"
                >
                    <div className="flex h-48 gap-1">
                        {/* Hours Column */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar rounded-xl bg-gray-50/50" ref={hourRef}>
                            <div className="px-1 py-1 space-y-1">
                                {hours.map(h => (
                                    <button
                                        key={h}
                                        data-value={h}
                                        type="button"
                                        onClick={() => handleTimeChange('hour', h)}
                                        className={`w-full py-2 rounded-lg text-sm font-bold transition flex items-center justify-center
                                                    ${currentH === h
                                                ? `${theme.bg} text-white shadow-md scale-95`
                                                : "text-gray-500 hover:bg-gray-100"}`}
                                    >
                                        {h}시
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-center text-gray-300 font-bold">:</div>

                        {/* Minutes Column */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar rounded-xl bg-gray-50/50" ref={minuteRef}>
                            <div className="px-1 py-1 space-y-1">
                                {minutes.map(m => (
                                    <button
                                        key={m}
                                        data-value={m}
                                        type="button"
                                        onClick={() => {
                                            handleTimeChange('minute', m);
                                        }}
                                        className={`w-full py-2 rounded-lg text-sm font-bold transition flex items-center justify-center
                                                    ${currentM === m
                                                ? `${theme.bg} text-white shadow-md scale-95`
                                                : "text-gray-500 hover:bg-gray-100"}`}
                                    >
                                        {String(m).padStart(2, '0')}분
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-gray-100 pt-2 flex justify-center">
                        <button
                            onClick={() => setIsOpen(false)}
                            className={`w-full py-2 rounded-xl text-xs font-bold ${theme.lightBg} ${theme.text} hover:opacity-80 transition`}
                        >
                            확인
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Add simple CSS for custom scrollbar in this component or relying on global
